import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
  useTheme,
  Pagination,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import { FeedOutlined, Refresh} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '@/store/types';
import { 
  FeedItem, 
  FeedSkeleton, 
  MediaPreviewModals 
} from '../dashboard/feed';
import { fetchMyFeeds } from '@/store';
import { feedService } from '@/services/feed/feedService';
import toast from 'react-hot-toast';

interface MyPostsListProps {
  search: string;
  type: string;
  startDate?: string;
  endDate?: string;
}

export const MyPostsList: React.FC<MyPostsListProps> = ({ search, type, startDate, endDate }) => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  
  const { feeds, pagination, isLoading, error } = useSelector(
    (state: RootState) => state.feed
  );

  const [updatingStatus, setUpdatingStatus] = useState<Set<string>>(new Set());

  
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = React.useState(false);
  const [imagePreview, setImagePreview] = useState<{ open: boolean; url: string; alt: string }>({ 
    open: false, url: '', alt: '' 
  });
  const [documentPreview, setDocumentPreview] = useState<{ open: boolean; url: string; fileName: string }>({ 
    open: false, url: '', fileName: '' 
  });
  const [expandedContent, setExpandedContent] = useState<Set<string>>(new Set());
  const [openCommentsFeedId, setOpenCommentsFeedId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch my feeds with search, type, and date filters
  useEffect(() => {
    const params = {
      page: currentPage,
      limit: 10,
      sort: '-updatedAt',
      ...(search && { search }),
      ...(type !== 'all' && { type }),
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
    };
    
    dispatch(fetchMyFeeds(params));
  }, [dispatch, search, type, startDate, endDate, currentPage]);

  // Track when we've completed the initial load
  useEffect(() => {
    if (!isLoading && !hasInitiallyLoaded) {
      setHasInitiallyLoaded(true);
    }
  }, [isLoading, hasInitiallyLoaded]);

  // Reset to page 1 when search, type, or date filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, type, startDate, endDate]);

  const handleRetry = () => {
    const params = {
      page: currentPage,
      limit: 10,
      sort: '-updatedAt',
      ...(search && { search }),
      ...(type !== 'all' && { type }),
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
    };
    dispatch(fetchMyFeeds(params));
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const handleImageClick = (url: string, alt: string) => {
    setImagePreview({ open: true, url, alt });
  };

  const handleDocumentClick = (url: string, fileName: string) => {
    setDocumentPreview({ open: true, url, fileName });
  };

  const handleStatusToggle = async (feedId: string, currentStatus: boolean) => {
    setUpdatingStatus(prev => new Set(prev).add(feedId));
    
    try {
      await feedService.updatePostStatus(feedId, !currentStatus);
      toast.success(`Post ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      
      // Refresh the feeds to get updated status
      const params = {
        page: currentPage,
        limit: 10,
        sort: '-updatedAt',
        ...(search && { search }),
        ...(type !== 'all' && { type }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
      };
      dispatch(fetchMyFeeds(params));
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to update post status');
    } finally {
      setUpdatingStatus(prev => {
        const newSet = new Set(prev);
        newSet.delete(feedId);
        return newSet;
      });
    }
  };

  // Show loading skeleton on initial load
  if (isLoading && !hasInitiallyLoaded) {
    return (
      <Box>
        {Array.from({ length: 3 }, (_, index) => (
          <FeedSkeleton key={index} />
        ))}
      </Box>
    );
  }

  // Show error state
  if (error && !feeds?.length) {
    return (
      <Paper 
        elevation={0}
        sx={{ 
          p: 4, 
          textAlign: 'center',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          variant="outlined" 
          onClick={handleRetry}
          startIcon={<Refresh />}
        >
          Try Again
        </Button>
      </Paper>
    );
  }

  // Show empty state
  if (!isLoading && (!feeds || feeds.length === 0)) {
    return (
      <Paper 
        elevation={0}
        sx={{ 
          p: 6, 
          textAlign: 'center',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        <FeedOutlined 
          sx={{ 
            fontSize: 48, 
            color: 'text.secondary', 
            mb: 2 
          }} 
        />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {search || type !== 'all' ? 'No posts match your filters' : 'No posts yet'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {search || type !== 'all' 
            ? 'Try adjusting your search or filter criteria'
            : 'Create your first post to get started!'
          }
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Feed Items */}
      {feeds && feeds.length > 0 ? (
        feeds.map((feed) => (
          <Box key={feed._id} sx={{ position: 'relative' }}>
            {/* Status Toggle */}
            <Box sx={{ 
              position: 'absolute', 
              top: 16, 
              right: 16, 
              zIndex: 1,
              backgroundColor: 'background.paper',
              borderRadius: 1,
              p: 1,
              boxShadow: 0
            }}>
              <Tooltip title={feed.isActive ? 'Deactivate post' : 'Activate post'}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={feed.isActive || false}
                      onChange={() => handleStatusToggle(feed._id, feed.isActive || false)}
                      disabled={updatingStatus.has(feed._id)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="caption" color="text.secondary">
                      {updatingStatus.has(feed._id) ? 'Updating...' : (feed.isActive ? 'Active' : 'Inactive')}
                    </Typography>
                  }
                  labelPlacement="start"
                  sx={{ margin: 0 }}
                />
              </Tooltip>
            </Box>

            <FeedItem
              feed={feed}
              expandedContent={expandedContent}
              setExpandedContent={setExpandedContent}
              onImageClick={handleImageClick}
              onDocumentClick={handleDocumentClick}
              openCommentsFeedId={openCommentsFeedId}
              setOpenCommentsFeedId={setOpenCommentsFeedId}
            />
          </Box>
        ))
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No feeds to display
        </Typography>
      )}

      {/* Loading More Indicator */}
      {isLoading && hasInitiallyLoaded && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={pagination.pages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Media Preview Modals */}
      <MediaPreviewModals
        imagePreview={imagePreview}
        documentPreview={documentPreview}
        onCloseImagePreview={() => setImagePreview({ open: false, url: '', alt: '' })}
        onCloseDocumentPreview={() => setDocumentPreview({ open: false, url: '', fileName: '' })}
      />
    </Box>
  );
};